import React, { useState, useEffect, useRef, useCallback } from "react";
import ScrollToHide from "./appbar/ScrollToHide";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import StoreSettingsAPI from "../../../api/firebase/StoreSettingsAPI";
import CouponsAPI from "../../../api/firebase/CouponsAPI";

const WHATSAPP_URL = "https://api.whatsapp.com/send/?phone=919411174186";
const PHONE_HREF = "tel:+919411174186";
const PHONE_LABEL = "+91\u20119411174186";

const SOCIAL_LINKS = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61559031269996",
    icon: FacebookIcon,
    className: "fb",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/inprovedahealthcare/",
    icon: InstagramIcon,
    className: "insta",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/inprovedahealthcare-products-b36a6037/",
    icon: LinkedInIcon,
    className: "linkedin",
  },
];

const SocialLinks = () => (
  <div className="site-topbar-social">
    {SOCIAL_LINKS.map((item) => {
      const Icon = item.icon;
      return (
        <a
          key={item.label}
          className={`site-topbar-icon ${item.className}`}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={item.label}
          title={item.label}
        >
          <Icon fontSize="inherit" />
        </a>
      );
    })}
  </div>
);

const SaleBanner = ({ isMobile }) => {
  const [activeMobileIndex, setActiveMobileIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [freeShippingAbove, setFreeShippingAbove] = useState(
    Number(process.env.REACT_APP_FREE_SHIPPING_ABOVE) || 500
  );
  const [couponAnnouncements, setCouponAnnouncements] = useState([]);
  const touchStartYRef = useRef(null);

  const promoAnnouncements = [
    {
      icon: LocalShippingOutlinedIcon,
      text: `Free Shipping on ₹${freeShippingAbove.toLocaleString("en-IN")}+ Orders`,
    },
    ...couponAnnouncements,
  ];
  const fixedAnnouncements = [
    {
      icon: SpaOutlinedIcon,
      text: "100% herbal & Ayurvedic products",
    },
    {
      icon: SupportAgentOutlinedIcon,
      text: "Need help? Chat with us on WhatsApp",
      href: WHATSAPP_URL,
    },
  ];
  const tickerItems = [...promoAnnouncements, promoAnnouncements[0]];

  useEffect(() => {
    new StoreSettingsAPI().getShippingSettings().then((result) => {
      const configuredThreshold = Number(result?.data?.freeShippingAbove);
      if (result?.success && Number.isFinite(configuredThreshold) && configuredThreshold >= 0) {
        setFreeShippingAbove(configuredThreshold);
      }
    });
  }, []);

  useEffect(() => {
    new CouponsAPI().getCoupons().then((result) => {
      if (!result?.success || !Array.isArray(result.data)) return;
      setCouponAnnouncements(
        result.data
          .filter((coupon) => coupon.isActive === true)
          .sort((a, b) => (Number(a.minShopping) || 0) - (Number(b.minShopping) || 0))
          .map((coupon) => ({
            icon: LocalOfferOutlinedIcon,
            text: `Flat ₹${Number(coupon.amount || 0).toLocaleString("en-IN")} off on ₹${Number(coupon.minShopping || 0).toLocaleString("en-IN")} order`,
          }))
      );
    });
  }, []);

  const goToNext = useCallback(() => {
    setIsTransitioning(true);
    setActiveMobileIndex((prev) => {
      if (prev >= promoAnnouncements.length) {
        return 1;
      }
      return prev + 1;
    });
  }, [promoAnnouncements.length]);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      goToNext();
    }, 4200);
    return () => clearInterval(timer);
  }, [isPaused, goToNext]);

  const handleTransitionEnd = () => {
    if (activeMobileIndex >= promoAnnouncements.length) {
      setIsTransitioning(false);
      setActiveMobileIndex(0);
    }
  };

  const handleTouchStart = (e) => {
    setIsPaused(true);
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    setIsPaused(false);
    if (touchStartYRef.current === null) return;
    const touchEndY = e.changedTouches[0].clientY;
    const diffY = touchEndY - touchStartYRef.current;
    if (diffY < -30) {
      goToNext();
    }
    touchStartYRef.current = null;
  };

  if (isMobile) {
    return (
      <div
        className="site-topbar site-topbar-mobile"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        role="region"
        aria-label="Promotions and Announcements"
      >
        <div className="site-topbar-vertical-ticker">
          <div
            className="site-topbar-vertical-track"
            style={{
              transform: `translateY(-${activeMobileIndex * 100}%)`,
              transition: isTransitioning
                ? "transform 0.85s cubic-bezier(0.25, 1, 0.5, 1)"
                : "none",
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {tickerItems.map((item, idx) => {
              const Icon = item.icon;
              const Tag = item.href ? "a" : "div";
              const isActive = idx === activeMobileIndex;
              return (
                <div
                  key={idx}
                  className={`site-topbar-vertical-slide ${
                    isActive ? "active" : ""
                  }`}
                >
                  <Tag
                    className="site-topbar-vertical-content"
                    {...(item.href
                      ? {
                          href: item.href,
                          target: "_blank",
                          rel: "noopener noreferrer",
                          title: item.text,
                        }
                      : {
                          role: "button",
                          tabIndex: 0,
                          onClick: goToNext,
                          onKeyDown: (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              goToNext();
                            }
                          },
                        })}
                  >
                    <Icon className="site-topbar-msg-icon" />
                    <span className="site-topbar-vertical-text">
                      {item.text}
                    </span>
                    {item.href ? (
                      <span className="site-topbar-vertical-arrow">›</span>
                    ) : null}
                  </Tag>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <ScrollToHide threshold={48}>
      <div className="site-topbar">
        <div className="site-topbar-inner">
          <div className="site-topbar-copy">
            <div className="site-topbar-desktop-promo-ticker">
              <div
                className="site-topbar-vertical-track"
                style={{
                  transform: `translateY(-${activeMobileIndex * 100}%)`,
                  transition: isTransitioning
                    ? "transform 0.85s cubic-bezier(0.25, 1, 0.5, 1)"
                    : "none",
                }}
                onTransitionEnd={handleTransitionEnd}
              >
                {tickerItems.map((item, idx) => {
                  const Icon = item.icon;
                  const isActive = idx === activeMobileIndex;
                  return (
                    <div
                      key={`${item.text}-${idx}`}
                      className={`site-topbar-vertical-slide ${isActive ? "active" : ""}`}
                    >
                      <span className="site-topbar-msg">
                        <Icon className="site-topbar-msg-icon" />
                        <span>{item.text}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            {fixedAnnouncements.map((item) => {
              const Icon = item.icon;
              const Tag = item.href ? "a" : "span";
              return (
                <Tag
                  key={item.text}
                  className={`site-topbar-msg ${
                    item.href ? "site-topbar-msg-link" : ""
                  }`}
                  href={item.href}
                  {...(item.href
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                >
                  <Icon className="site-topbar-msg-icon" />
                  <span>{item.text}</span>
                </Tag>
              );
            })}
          </div>
          <div className="site-topbar-actions">
            <a
              className="site-topbar-chip site-topbar-chip-whatsapp"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              title="Chat on WhatsApp"
            >
              <span className="site-topbar-live-dot" />
              <WhatsAppIcon className="site-topbar-chip-icon" />
              <span>WhatsApp</span>
            </a>
            <a
              className="site-topbar-chip site-topbar-chip-ghost"
              href={PHONE_HREF}
              title="Call us"
            >
              <PhoneOutlinedIcon className="site-topbar-chip-icon" />
              <span className="site-topbar-phone-label">{PHONE_LABEL}</span>
            </a>
            <SocialLinks />
          </div>
        </div>
      </div>
    </ScrollToHide>
  );
};

export default SaleBanner;
